import { useRef } from 'react';
import {
    motion,
    useScroll,
    useSpring,
    useTransform,
    useReducedMotion,
} from 'framer-motion';
import SectionWrapper from '../ui/SectionWrapper';
import useMediaQuery from '../../hooks/useMediaQuery';
import { timelineData } from '../../data/timeline';

const isTechItem = (item) => item.id === 1 || item.id === 2; // Tech experiences

// Tailwind only generates classes it can see literally in the source,
// so pick between full class names — never build them with template strings
const ringBorderClass = (item) =>
    isTechItem(item) ? 'border-accent-blue/40' : 'border-accent-gold/40';

const DescriptionText = ({ item }) =>
    item.link ? (
        <>
            {item.descriptionBefore}
            <a
                href={item.link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-accent-blue hover:text-white transition-colors underline decoration-accent-blue/40 hover:decoration-white font-medium"
            >
                {item.link.text}
            </a>
            {item.descriptionAfter}
        </>
    ) : (
        item.description
    );

// Shared glass card body (period pill, title, description)
const CardBody = ({ item, showMobileIcon = false }) => (
    <div className="relative bg-bg-elevated/80 backdrop-blur-xl p-6 md:p-8 rounded-2xl h-full border border-white/5 hover:border-white/10 transition-colors">
        <div className="flex flex-wrap items-center gap-3 mb-4">
            <span className="px-3 py-1 text-xs font-bold tracking-wider rounded-full bg-accent-blue/15 text-accent-blue border border-accent-blue/20">
                {item.period}
            </span>
            {showMobileIcon && (
                <div className="md:hidden flex items-center gap-2 text-text-muted">
                    <item.icon className="text-accent-gold" />
                </div>
            )}
        </div>

        <h3 className="text-2xl font-heading font-bold text-white mb-4 group-hover:text-accent-gold transition-colors duration-300">
            {item.title}
        </h3>

        {(item.description || item.link) && (
            <p className="text-text-secondary leading-relaxed text-sm md:text-base">
                <DescriptionText item={item} />
            </p>
        )}
    </div>
);

const GlassCard = ({ item, showMobileIcon }) => (
    <motion.div
        className="relative group p-[1px] rounded-2xl bg-gradient-to-br from-white/10 to-transparent overflow-hidden"
        whileHover={{ scale: 1.02 }}
        transition={{ type: 'spring', stiffness: 300, damping: 20 }}
    >
        <div className="absolute inset-0 bg-gradient-to-br from-accent-blue/10 to-accent-gold/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
        <CardBody item={item} showMobileIcon={showMobileIcon} />
    </motion.div>
);

/* ============================================================
   Vertical timeline — mobile / reduced-motion / short-viewport
   fallback. Scroll-scrubbed line draw + per-item entrances.
   ============================================================ */

const VerticalItem = ({ item, index, prefersReducedMotion }) => {
    const itemRef = useRef(null);

    // Per-item scroll progress: 0 when the item's top edge is 90% down the
    // viewport, 1 when it reaches the viewport's vertical center — matching
    // the tip of the drawn line.
    const { scrollYProgress: itemProgress } = useScroll({
        target: itemRef,
        offset: ['start 0.9', 'start center'],
    });

    const cardY = useTransform(itemProgress, [0, 1], [40, 0]);
    const cardOpacity = useTransform(itemProgress, [0, 1], [0, 1]);
    const nodeGlow = useTransform(itemProgress, [0.6, 1], [0, 1]);
    const nodeScale = useTransform(itemProgress, [0.6, 1], [0.8, 1]);

    const cardStyle = prefersReducedMotion ? {} : { y: cardY, opacity: cardOpacity };
    const glowStyle = prefersReducedMotion
        ? { opacity: 1, scale: 1 }
        : { opacity: nodeGlow, scale: nodeScale };

    const isEven = index % 2 === 0;

    return (
        <div
            ref={itemRef}
            className={`relative flex items-center md:items-start gap-6 md:gap-12 ${isEven ? 'md:flex-row' : 'md:flex-row-reverse'
                }`}
        >
            {/* Mobile: Node on left — dim base + scroll-lit glow layer */}
            <div className="md:hidden absolute left-8 transform -translate-x-1/2 flex items-center justify-center z-20">
                <motion.div className="absolute inset-0 flex items-center justify-center" style={glowStyle}>
                    <motion.div
                        className="absolute w-12 h-12 rounded-full border-2 border-accent-blue/30"
                        animate={{ scale: [1, 1.5, 1], opacity: [0.5, 0, 0.5] }}
                        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                    />
                </motion.div>
                <div className="w-8 h-8 rounded-full bg-bg-primary border-2 border-white/10 flex items-center justify-center">
                    <div className="w-3 h-3 rounded-full bg-accent-blue/40" />
                </div>
                {/* Glow layer: full-strength styling pre-rendered, opacity scrubbed
                    (we never animate box-shadow itself — compositor-friendly) */}
                <motion.div
                    className="absolute w-8 h-8 rounded-full bg-bg-primary border-2 border-accent-gold flex items-center justify-center shadow-[0_0_15px_rgba(251,191,36,0.5)]"
                    style={glowStyle}
                >
                    <div className="w-3 h-3 rounded-full bg-accent-blue" />
                </motion.div>
            </div>

            {/* Desktop: Node in center — same dim base + glow layer pattern */}
            <div className="hidden md:flex absolute left-1/2 transform -translate-x-1/2 items-center justify-center z-20 mt-6">
                <motion.div className="absolute inset-0 flex items-center justify-center" style={glowStyle}>
                    <motion.div
                        className={`absolute w-16 h-16 rounded-full border-2 ${ringBorderClass(item)}`}
                        animate={{ scale: [1, 1.6, 1], opacity: [0.6, 0, 0.6] }}
                        transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut", delay: index * 0.2 }}
                    />
                </motion.div>
                <div className="w-12 h-12 rounded-full bg-bg-primary border-[3px] border-white/10 flex items-center justify-center">
                    <item.icon className="text-text-muted text-lg" />
                </div>
                <motion.div
                    className="absolute w-12 h-12 rounded-full bg-bg-primary border-[3px] border-accent-blue flex items-center justify-center shadow-[0_0_20px_rgba(59,130,246,0.5)]"
                    style={glowStyle}
                >
                    <item.icon className="text-accent-gold text-lg" />
                </motion.div>
            </div>

            {/* Content Side — entrance scrubbed by scroll */}
            <motion.div
                className={`flex-1 md:w-1/2 ml-12 md:ml-0 ${isEven ? 'md:pr-12' : 'md:pl-12'}`}
                style={cardStyle}
            >
                <GlassCard item={item} showMobileIcon />
            </motion.div>

            {/* Desktop: Empty side for spacing */}
            <div className="hidden md:block md:w-1/2" />
        </div>
    );
};

const VerticalTimeline = ({ prefersReducedMotion }) => {
    const timelineRef = useRef(null);

    const { scrollYProgress } = useScroll({
        target: timelineRef,
        offset: ['start center', 'end center'],
    });
    const lineScale = useSpring(scrollYProgress, {
        stiffness: 100,
        damping: 30,
        restDelta: 0.001,
    });

    const lineStyle = prefersReducedMotion ? { scaleY: 1 } : { scaleY: lineScale };

    return (
        <SectionWrapper id="experience" animate={false} className="bg-bg-secondary/30 relative overflow-hidden">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-accent-blue/5 rounded-full blur-[120px] pointer-events-none" />

            <motion.h2
                className="text-3xl md:text-5xl font-heading font-bold mb-16 text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
            >
                <span className="text-gradient">Experience</span>
            </motion.h2>

            <div ref={timelineRef} className="relative max-w-5xl mx-auto">
                {/* Timeline: static dim track + scroll-drawn glowing fill */}
                <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 w-1 h-full rounded-full bg-gradient-to-b from-accent-blue via-accent-gold to-accent-blue opacity-15" />
                <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 w-1 h-full opacity-10 blur-[4px] rounded-full bg-gradient-to-b from-accent-blue via-accent-gold to-accent-blue" />
                {/* Fill — framer-motion owns the inline transform, which would
                    override Tailwind's -translate-x-1/2, so center with -ml-0.5 */}
                <motion.div
                    className="hidden md:block absolute left-1/2 -ml-0.5 w-1 h-full origin-top rounded-full bg-gradient-to-b from-accent-blue via-accent-gold to-accent-blue shadow-[0_0_15px_rgba(59,130,246,0.6)]"
                    style={lineStyle}
                />
                <div className="md:hidden absolute left-8 w-1 h-full rounded-full bg-gradient-to-b from-accent-blue via-accent-gold to-accent-blue opacity-15" />
                <motion.div
                    className="md:hidden absolute left-8 w-1 h-full origin-top rounded-full bg-gradient-to-b from-accent-blue via-accent-gold to-accent-blue shadow-[0_0_15px_rgba(59,130,246,0.6)]"
                    style={lineStyle}
                />

                <div className="space-y-12 md:space-y-24">
                    {timelineData.map((item, index) => (
                        <VerticalItem
                            key={item.id}
                            item={item}
                            index={index}
                            prefersReducedMotion={prefersReducedMotion}
                        />
                    ))}
                </div>
            </div>
        </SectionWrapper>
    );
};

/* ============================================================
   Horizontal journey — desktop. The section pins to the
   viewport and vertical scroll becomes the story's "time":
   the line travels node to node, one card in focus at a time.
   ============================================================ */

// Chronological order (oldest → newest), left to right
const JOURNEY_ITEMS = [...timelineData].reverse();
const N = JOURNEY_ITEMS.length;
const SEG = 1 / N;
// Horizontal position of node i along the track (fraction 0→1)
const nodeFrac = (i) => (i + 0.5) / N;

const JourneyCard = ({ item, index, progress }) => {
    const start = index * SEG;
    const isFirst = index === 0;
    const isLast = index === N - 1;
    // Each card owns an equal slice of the journey, but its enter window
    // reaches back into the previous card's exit window — the two overlap,
    // so one card cross-fades into the next instead of a hard swap. First
    // card is there from the start; last card never exits — the journey
    // ends on the strongest chapter.
    const enterKeys = isFirst
        ? [0, 0.1 * SEG]
        : [start - 0.12 * SEG, start + 0.18 * SEG];
    const keys = isLast
        ? enterKeys
        : [...enterKeys, start + 0.82 * SEG, start + 1.12 * SEG];

    const opacity = useTransform(progress, keys, isLast ? [0, 1] : [0, 1, 1, 0]);
    const x = useTransform(progress, keys, isLast ? [140, 0] : [140, 0, 0, -140]);
    const scale = useTransform(progress, keys, isLast ? [0.95, 1] : [0.95, 1, 1, 0.92]);
    // Invisible cards must not swallow clicks on the card below them
    const pointerEvents = useTransform(opacity, (v) => (v > 0.5 ? 'auto' : 'none'));

    return (
        // Positioning wrapper fills the band between title and timeline and
        // vertically centers whatever card height this chapter has; it must
        // not intercept clicks itself (the motion card decides that)
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <motion.div
                className="w-[min(38rem,calc(100%-3rem))] z-20"
                style={{ opacity, x, scale, pointerEvents }}
            >
                <GlassCard item={item} />
            </motion.div>
        </div>
    );
};

const JourneyNode = ({ item, index, lineP }) => {
    const frac = nodeFrac(index);
    // The node lights up exactly as the line's tip reaches it
    const glow = useTransform(lineP, [frac - 0.05, frac], [0, 1]);
    const glowScale = useTransform(lineP, [frac - 0.05, frac], [0.85, 1]);
    const glowStyle = { opacity: glow, scale: glowScale };

    return (
        <div
            className="absolute top-1/2 transform -translate-x-1/2 -translate-y-1/2 flex items-center justify-center z-10"
            style={{ left: `${frac * 100}%` }}
        >
            <motion.div className="absolute inset-0 flex items-center justify-center" style={glowStyle}>
                <motion.div
                    className={`absolute w-16 h-16 rounded-full border-2 ${ringBorderClass(item)}`}
                    animate={{ scale: [1, 1.6, 1], opacity: [0.6, 0, 0.6] }}
                    transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut", delay: index * 0.2 }}
                />
            </motion.div>
            <div className="w-12 h-12 rounded-full bg-bg-primary border-[3px] border-white/10 flex items-center justify-center">
                <item.icon className="text-text-muted text-lg" />
            </div>
            <motion.div
                className="absolute w-12 h-12 rounded-full bg-bg-primary border-[3px] border-accent-blue flex items-center justify-center shadow-[0_0_20px_rgba(59,130,246,0.5)]"
                style={glowStyle}
            >
                <item.icon className="text-accent-gold text-lg" />
            </motion.div>
            <span className="absolute top-full mt-4 left-1/2 -translate-x-1/2 text-xs font-mono tracking-widest text-text-muted">
                {item.year}
            </span>
        </div>
    );
};

const JourneyYear = ({ item, index, progress }) => {
    const start = index * SEG;
    const isFirst = index === 0;
    const isLast = index === N - 1;
    const keys = isFirst
        ? [start, start + 0.8 * SEG, start + SEG]
        : isLast
            ? [start, start + 0.2 * SEG]
            : [start, start + 0.2 * SEG, start + 0.8 * SEG, start + SEG];
    const values = isFirst ? [1, 1, 0] : isLast ? [0, 1] : [0, 1, 1, 0];
    const opacity = useTransform(progress, keys, values);

    return (
        <motion.span
            className="absolute text-[18vw] leading-none font-heading font-bold text-white/5 select-none"
            style={{ opacity }}
        >
            {item.year}
        </motion.span>
    );
};

const HorizontalJourney = () => {
    const wrapperRef = useRef(null);

    // Progress through the whole pinned journey: 0 when the tall wrapper's
    // top hits the viewport top (pin engages), 1 when its bottom does (pin
    // releases). Small buffer zones at both ends so the visitor isn't
    // thrown straight into the choreography.
    const { scrollYProgress } = useScroll({
        target: wrapperRef,
        offset: ['start start', 'end end'],
    });
    const progress = useTransform(scrollYProgress, [0.04, 0.96], [0, 1]);
    // Every scrubbed element is driven by this spring-smoothed copy of the
    // progress, not by the raw value: the mouse wheel moves in discrete
    // steps, and without the spring interpolating between them the cards
    // would jump "frame to frame" instead of fading fluidly
    const smoothP = useSpring(progress, { stiffness: 90, damping: 30, restDelta: 0.001 });

    // Background chapter tint: gold while the story is in law, blue once it
    // crosses into tech (chronologically that switch is ~item 3 of 5)
    const goldOpacity = useTransform(smoothP, [0.5, 0.7], [0.6, 0]);
    const blueOpacity = useTransform(smoothP, [0.5, 0.7], [0, 0.6]);
    const glowX = useTransform(smoothP, [0, 1], ['-8%', '8%']);
    const hintOpacity = useTransform(progress, [0, 0.03], [1, 0]);

    return (
        // The tall wrapper "costs" ~85vh of scroll per chapter — that scroll
        // is the animation's timeline while the scene inside stays pinned
        <section
            id="experience"
            ref={wrapperRef}
            className="relative bg-bg-secondary/30"
            style={{ height: `${N * 85 + 30}vh` }}
        >
            <div className="sticky top-0 h-screen overflow-hidden">
                {/* Layer 1: parallax chapter glows */}
                <motion.div className="absolute inset-0 pointer-events-none" style={{ x: glowX }}>
                    <motion.div
                        className="absolute top-1/3 left-1/4 w-[700px] h-[700px] bg-accent-gold/10 rounded-full blur-[120px]"
                        style={{ opacity: goldOpacity }}
                    />
                    <motion.div
                        className="absolute top-1/3 right-1/4 w-[700px] h-[700px] bg-accent-blue/10 rounded-full blur-[120px]"
                        style={{ opacity: blueOpacity }}
                    />
                </motion.div>

                {/* Layer 2: giant background year */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    {JOURNEY_ITEMS.map((item, index) => (
                        <JourneyYear key={item.id} item={item} index={index} progress={smoothP} />
                    ))}
                </div>

                {/* Layer 3: title */}
                <motion.h2
                    className="relative z-30 pt-28 md:pt-32 text-3xl md:text-5xl font-heading font-bold text-center"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                >
                    <span className="text-gradient">Experience</span>
                </motion.h2>

                {/* Layer 4: the cards — one in focus at a time, stacked in the
                    same spot; entrance/exit motion tells the travel story */}
                {/* The cards live in the band between title and timeline,
                    vertically centered so spacing stays balanced no matter
                    how tall each chapter's text is */}
                <div className="absolute inset-x-0 top-[26%] bottom-[25%]">
                    {JOURNEY_ITEMS.map((item, index) => (
                        <JourneyCard key={item.id} item={item} index={index} progress={smoothP} />
                    ))}
                </div>

                {/* Layer 5: the horizontal line + nodes */}
                <div className="absolute left-[6%] right-[6%] top-[81%]">
                    <div className="relative h-1">
                        <div className="absolute inset-0 rounded-full bg-gradient-to-r from-accent-blue via-accent-gold to-accent-blue opacity-15" />
                        <div className="absolute inset-0 rounded-full bg-gradient-to-r from-accent-blue via-accent-gold to-accent-blue opacity-10 blur-[4px]" />
                        <motion.div
                            className="absolute inset-0 origin-left rounded-full bg-gradient-to-r from-accent-blue via-accent-gold to-accent-blue shadow-[0_0_15px_rgba(59,130,246,0.6)]"
                            style={{ scaleX: smoothP }}
                        />
                        {JOURNEY_ITEMS.map((item, index) => (
                            <JourneyNode key={item.id} item={item} index={index} lineP={smoothP} />
                        ))}
                    </div>
                </div>

                {/* Layer 6: one-time scroll hint */}
                <motion.div
                    className="absolute bottom-6 inset-x-0 flex flex-col items-center gap-1 text-text-muted pointer-events-none"
                    style={{ opacity: hintOpacity }}
                >
                    <span className="text-xs tracking-widest uppercase">Scroll</span>
                    <motion.svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        animate={{ y: [0, 6, 0] }}
                        transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </motion.svg>
                </motion.div>
            </div>
        </section>
    );
};

const Experience = () => {
    const prefersReducedMotion = useReducedMotion();
    // The pinned journey needs a desktop-sized viewport with room to
    // breathe; everyone else gets the vertical timeline
    const canJourney = useMediaQuery('(min-width: 768px) and (min-height: 600px)');

    return canJourney && !prefersReducedMotion ? (
        <HorizontalJourney />
    ) : (
        <VerticalTimeline prefersReducedMotion={prefersReducedMotion} />
    );
};

export default Experience;
